import type { ComponentId } from '@highereducation/hen-ui';
import type {
  InteractionRegistry,
  RegistryPolicy,
  UnknownComponentPolicy,
  ViewRegistry,
} from '../registry/types';
import type {
  EventingMetadata,
  NormalizedInteraction,
  NormalizedView,
} from '../types/normalized';

export interface UnknownComponentHandler {
  readonly policy: RegistryPolicy;
  readonly onUnknownComponentId?: (componentId: string) => void;
}

export function applyUnknownComponentPolicy(
  componentId: string,
  handler: UnknownComponentHandler,
): void {
  const policy: UnknownComponentPolicy = handler.policy.unknownComponent;
  if (policy === 'ignore') {
    return;
  }

  if (policy === 'warn' && typeof process !== 'undefined') {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[eventing] Unmapped componentId "${componentId}" (no registry entry).`,
      );
    }
  }

  if (policy === 'error' && typeof process !== 'undefined') {
    if (process.env.NODE_ENV !== 'production') {
      console.error(
        `[eventing] Unmapped componentId "${componentId}" (no registry entry).`,
      );
    }
  }

  handler.onUnknownComponentId?.(componentId);
}

export function buildNormalizedInteraction(
  componentId: ComponentId,
  eventKey: string,
  timestamp: number = Date.now(),
  metadata?: EventingMetadata,
): NormalizedInteraction {
  return {
    kind: 'interaction',
    componentId,
    eventKey,
    timestamp,
    ...(metadata ? { metadata } : {}),
  };
}

export function buildNormalizedView(
  componentId: ComponentId,
  eventKey: string,
  timestamp: number = Date.now(),
  metadata?: EventingMetadata,
): NormalizedView {
  return {
    kind: 'view',
    componentId,
    eventKey,
    timestamp,
    ...(metadata ? { metadata } : {}),
  };
}

export interface RouteEventOptions {
  readonly metadata?: EventingMetadata;
}

export function routeInteraction(
  componentId: string,
  interactionRegistry: InteractionRegistry,
  handler: UnknownComponentHandler,
  options?: RouteEventOptions,
): NormalizedInteraction | null {
  const entry = interactionRegistry.get(componentId as ComponentId);
  if (!entry) {
    applyUnknownComponentPolicy(componentId, handler);
    return null;
  }

  return buildNormalizedInteraction(
    entry.componentId,
    entry.eventKey,
    Date.now(),
    options?.metadata,
  );
}

export function routeView(
  componentId: string,
  viewRegistry: ViewRegistry,
  handler: UnknownComponentHandler,
  options?: RouteEventOptions,
): NormalizedView | null {
  const entry = viewRegistry.get(componentId as ComponentId);
  if (!entry) {
    applyUnknownComponentPolicy(componentId, handler);
    return null;
  }

  return buildNormalizedView(
    entry.componentId,
    entry.eventKey,
    Date.now(),
    options?.metadata,
  );
}
